import React from 'react';
import { useParams } from 'react-router-dom';
import GeneralSection from './GeneralSection';
import PersonalizedSection from './PersonalizedSection';
import useDrawOffers from '../hooks/useDrawOffers';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import { AddressDisplayLong } from './AddressDisplay';

const OneOnOneGamesSection = ({
  // All the props from the original GeneralSection implementation
  ...props
}) => {
  const { gameName } = useParams();
  
  // Extract needed props for PersonalizedSection
  const {
    queriedOffers,
    queriedGameInstances, 
    acceptOffer,
    killGame,
    offerDraw,
    acceptDrawOffer,
    withdrawDrawOffer,
    playerAccount,
    ethersUtils,
    gameUrls,
    provider,
    signer,
    createContract,
    GameMakerConfig,
    gameMakerContractAddress,
    games,
    // Personalized data - unfiltered data from Dapp.js for PersonalizedSection
    personalizedGameInstances,
    personalizedOffers
  } = props;

  // Get real-time draw offers data
  const { drawOffers } = useDrawOffers(playerAccount, personalizedGameInstances || []);

  // Filter data based on selected game (if any)
  const filteredOffers = gameName 
    ? queriedOffers.filter(offer => offer.gameName === gameName)
    : queriedOffers;
    
  const filteredGameInstances = gameName
    ? queriedGameInstances.filter(instance => instance.gameName === gameName) 
    : queriedGameInstances;

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">
          {gameName ? `${gameName} Games` : '1:1 Competitive Games'}
        </h2>
        <p className="text-gray-600">
          {gameName
            ? `Create offers and join ${gameName} games with other players.`
            : 'Challenge other players head-to-head in skill-based games with equal stakes.'
          }
        </p>
      </div>

      {/* Personalized Section - shows user's personalized content */}
      <PersonalizedSection
        queriedOffers={personalizedOffers || []}
        queriedGameInstances={personalizedGameInstances || []}
        drawOffers={drawOffers}
        playerAccount={playerAccount}
        ethersUtils={ethersUtils}
        acceptOffer={acceptOffer}
        killGame={killGame}
        offerDraw={offerDraw}
        acceptDrawOffer={acceptDrawOffer}
        withdrawDrawOffer={withdrawDrawOffer}
        gameUrls={gameUrls}
        provider={provider}
        signer={signer}
        createContract={createContract}
        GameMakerConfig={GameMakerConfig}
        gameMakerContractAddress={gameMakerContractAddress}
        games={games}
      />
      
      {/* General Section - always visible with editable filters */}
      <GeneralSection
        {...props}
        // Override with filtered data if specific game is selected
        queriedOffers={filteredOffers}
        queriedGameInstances={filteredGameInstances}
      />

      {/* Contract Information Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h3 className="text-xl font-semibold mb-4 text-gray-700">Contract Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">GameMaker Contract</p>
            <div className="font-mono text-xs">
              <AddressDisplayLong address={contractAddresses.GameMaker} useExplorerLink={true} />
            </div>
            <p className="text-xs text-gray-500 mt-1">Handles game offers, bets, and matchmaking</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">GameRegistry Contract</p>
            <div className="font-mono text-xs">
              <AddressDisplayLong address={contractAddresses.GameRegistry} useExplorerLink={true} />
            </div>
            <p className="text-xs text-gray-500 mt-1">Registry of available games and configurations</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneOnOneGamesSection;